// ============================================================================
// Línea de tiempo del ciclo de desarrollo (#559). Paridad con CC-CONSOLE.
// ============================================================================
// No pinta nada que no venga en `steps`: el backend ya decidió qué va en cada
// proyección (la del cliente no trae enlaces, ni autor, ni notas), así que una
// vista de cliente no puede filtrar un dato técnico "por descuido".
// ============================================================================
import { useTranslation } from "react-i18next";
import { formatDate } from "../services/serviceDeskService";
import { currentStageColor, type DevStage, type StageStep } from "../services/devStages";

function StepDot({ step }: { step: StageStep }) {
  const reached = step.status === "done" || step.status === "current";
  const color = reached ? step.color : "#cbd5e1";
  return (
    <span
      aria-hidden
      className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2"
      style={{
        borderColor: color,
        backgroundColor: reached ? color : "transparent",
        boxShadow: step.status === "current" ? `0 0 0 3px ${color}33` : undefined,
      }}
    />
  );
}

export interface StageTimelineProps {
  steps: StageStep[];
  /** Etapa actual, para el texto accesible. */
  currentLabel?: string;
  compact?: boolean;
  isDarkMode?: boolean;
}

export function StageTimeline({ steps, currentLabel, compact = false, isDarkMode = false }: StageTimelineProps) {
  const { t } = useTranslation("serviceDesk");
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const text = isDarkMode ? "text-gray-100" : "text-gray-900";

  if (!steps || steps.length === 0) {
    return <p className={`text-xs ${muted}`}>{t("ticketDetail.stage.empty")}</p>;
  }

  return (
    <ol className="space-y-0" aria-label={t("ticketDetail.stage.timelineLabel", { label: currentLabel || "" })}>
      {steps.map((step, i) => {
        const last = i === steps.length - 1;
        const reached = step.status !== "pending";
        return (
          <li key={step.id} className="flex gap-3" data-status={step.status}>
            <div className="flex flex-col items-center">
              <StepDot step={step} />
              {!last && (
                <span
                  aria-hidden
                  className="w-0.5 flex-1"
                  style={{ minHeight: compact ? 14 : 20, backgroundColor: reached ? step.color : "#e2e8f0" }}
                />
              )}
            </div>
            <div className={`${last ? "" : "pb-2"} -mt-0.5`}>
              <p
                className={`text-sm ${step.status === "current" ? "font-semibold" : "font-medium"} ${
                  reached ? text : muted
                }`}
              >
                {step.label}
                {step.status === "current" && (
                  <span
                    className="ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    style={{ color: step.color, backgroundColor: `${step.color}1a` }}
                  >
                    {t("ticketDetail.stage.current")}
                  </span>
                )}
              </p>
              <p className={`text-[11px] ${muted}`}>{step.at ? formatDate(step.at) : t("ticketDetail.stage.pending")}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Chip compacto con la etapa actual, para la cabecera de la ficha. */
export function StageBadge({ devStage }: { devStage?: DevStage | null }) {
  if (!devStage?.current) return null;
  const color = currentStageColor(devStage);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color, backgroundColor: `${color}1a` }}
      title={devStage.label}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {devStage.label}
    </span>
  );
}
